/**
 * WelcomeAudioCard — concierge welcome audio surfaced for a specific
 * member by Michael (e.g. Faye). Renders only if `/api/welcome` returns
 * a doc; otherwise null, so no extra noise for everyone else.
 */
import { useEffect, useState, useRef } from "react";
import { Play, Pause, Download, Sparkles } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

export default function WelcomeAudioCard() {
  const [welcome, setWelcome] = useState(null);
  const [playing, setPlaying] = useState(false);
  // The Bearer-protected audio endpoint can't be hit by <audio src=...>.
  // Workaround: fetch the bytes once, build an object URL, stream that.
  const [objectUrl, setObjectUrl] = useState(null);
  const audioRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/welcome`, { headers: auth(), cache: "no-store" });
        if (r.ok && !cancelled) setWelcome(await r.json());
      } catch (_) { /* silent — most members won't have one */ }
    })();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!welcome) return;
    let cancelled = false;
    let lastUrl = null;
    (async () => {
      try {
        const r = await fetch(`${API}/api/welcome/audio`, { headers: auth(), cache: "no-store" });
        if (!r.ok || cancelled) return;
        const blob = await r.blob();
        lastUrl = URL.createObjectURL(blob);
        if (!cancelled) setObjectUrl(lastUrl);
      } catch (_) { /* leave card with title only */ }
    })();
    return () => {
      cancelled = true;
      if (lastUrl) URL.revokeObjectURL(lastUrl);
    };
  }, [welcome]);

  if (!welcome) return null;

  const toggle = () => {
    if (!audioRef.current) return;
    if (playing) audioRef.current.pause();
    else audioRef.current.play().catch(() => {});
  };

  return (
    <div
      className="rounded-2xl bg-gradient-to-br from-amber-950/95 via-amber-900/85 to-orange-950/95 ring-1 ring-amber-700/30 px-5 py-5 shadow-lg shadow-amber-950/40"
      data-testid="welcome-audio-card"
    >
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-amber-500/15 ring-1 ring-amber-400/30 flex items-center justify-center shrink-0">
          <Sparkles size={22} className="text-amber-300" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-[10px] uppercase tracking-[0.25em] text-amber-300/70 mb-0.5">
            A note from Michael
          </p>
          <h3 className="text-white font-medium text-base">{welcome.title}</h3>
          <p className="text-amber-100/70 text-xs mt-0.5">{welcome.subtitle}</p>
        </div>
      </div>

      <div className="flex items-center gap-3 mt-4">
        <button
          onClick={toggle}
          disabled={!objectUrl}
          className="w-12 h-12 rounded-full bg-gradient-to-br from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 flex items-center justify-center shadow-lg shadow-amber-900/40 disabled:opacity-50"
          data-testid="welcome-play-btn"
          aria-label={playing ? "pause" : "play"}
        >
          {playing
            ? <Pause size={20} className="text-amber-950" />
            : <Play size={20} className="text-amber-950 ml-0.5" fill="currentColor" />}
        </button>
        <div className="flex-1 min-w-0">
          {objectUrl ? (
            <audio
              ref={audioRef}
              src={objectUrl}
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
              onEnded={() => setPlaying(false)}
              controls
              preload="metadata"
              className="w-full"
              data-testid="welcome-native-audio"
            />
          ) : (
            <p className="text-amber-200/60 text-xs">Preparing your welcome…</p>
          )}
        </div>
      </div>

      <a
        href={`${API}/api/welcome/audio?download=1`}
        className="inline-flex items-center gap-1.5 mt-3 text-amber-300/80 hover:text-amber-200 text-xs"
        data-testid="welcome-download-link"
        onClick={(e) => {
          // <a download> can't carry Authorization header. Fetch + blob.
          e.preventDefault();
          fetch(`${API}/api/welcome/audio?download=1`, { headers: auth() })
            .then((r) => r.blob())
            .then((blob) => {
              const url = URL.createObjectURL(blob);
              const a = document.createElement("a");
              a.href = url;
              a.download = `welcome-${(welcome.title || "audio").replace(/\s+/g, "-").toLowerCase()}.mp3`;
              document.body.appendChild(a);
              a.click();
              a.remove();
              URL.revokeObjectURL(url);
            });
        }}
      >
        <Download size={12} /> Save to your phone
      </a>
    </div>
  );
}
